import React, { useCallback, useEffect, useMemo, useState } from 'react';

const API_BASE = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

function authHeaders(json = true) {
  return {
    ...(json ? { 'Content-Type': 'application/json' } : {}),
    Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
  };
}

async function apiRequest(path, options = {}) {
  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: { ...authHeaders(options.body !== undefined), ...(options.headers || {}) },
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(payload.detail || 'The creditor cleanup request failed.');
  return payload;
}

function formatAddress(item) {
  if (!item) return 'No address';
  const locality = [item.city, item.state].filter(Boolean).join(', ');
  return [item.street1, item.street2, [locality, item.zip_code].filter(Boolean).join(' ')]
    .filter(Boolean)
    .join(' · ');
}

function formatDate(value) {
  if (!value) return 'Not recorded';
  const parsed = new Date(`${String(value).slice(0, 10)}T12:00:00`);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleDateString();
}

function SmallButton({ children, onClick, disabled, tone = 'neutral', type = 'button' }) {
  const tones = {
    primary: 'border-indigo-600 bg-indigo-600 text-white hover:bg-indigo-700',
    danger: 'border-red-300 bg-white text-red-700 hover:bg-red-50',
    success: 'border-green-300 bg-white text-green-700 hover:bg-green-50',
    neutral: 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50',
  };
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`rounded-md border px-3 py-1.5 text-xs font-semibold disabled:cursor-not-allowed disabled:opacity-50 ${tones[tone]}`}
    >
      {children}
    </button>
  );
}

function StatusPill({ children, tone = 'gray' }) {
  const colors = {
    gray: 'bg-gray-100 text-gray-700',
    red: 'bg-red-100 text-red-800',
    amber: 'bg-amber-100 text-amber-800',
    green: 'bg-green-100 text-green-800',
    blue: 'bg-blue-100 text-blue-800',
  };
  return <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${colors[tone]}`}>{children}</span>;
}

function CreditorSummary({ creditor, selected, onSelect, selectable = false }) {
  return (
    <label className={`block rounded-lg border p-3 ${selected ? 'border-indigo-400 bg-indigo-50' : 'border-gray-200 bg-white'}`}>
      <div className="flex items-start gap-3">
        {selectable && (
          <input
            type="radio"
            checked={selected}
            onChange={onSelect}
            className="mt-1 h-4 w-4 border-gray-300 text-indigo-600"
          />
        )}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-semibold text-gray-900">{creditor.account_name}</span>
            {creditor.is_verified && <StatusPill tone="green">Verified</StatusPill>}
            {creditor.source && <StatusPill>{creditor.source_label || creditor.source}</StatusPill>}
          </div>
          <div className="mt-1 text-sm text-gray-700">{formatAddress(creditor)}</div>
          <div className="mt-2 text-xs text-gray-500">
            Used with this exact address {creditor.usage_count || 0} time(s)
            {creditor.last_used ? ` · last used ${formatDate(creditor.last_used)}` : ''}
            {creditor.name_usage_count > creditor.usage_count
              ? ` · name appears ${creditor.name_usage_count} time(s) overall`
              : ''}
          </div>
        </div>
      </div>
    </label>
  );
}

function EditModal({ entry, addressTypes, saving, onClose, onSave }) {
  const [form, setForm] = useState(entry);
  if (!entry) return null;
  const update = (field, value) => setForm((current) => ({ ...current, [field]: value }));
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <form
        onSubmit={(event) => {
          event.preventDefault();
          onSave(form);
        }}
        className="relative max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-white p-6 shadow-xl"
      >
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Edit common creditor</h3>
          <button type="button" onClick={onClose} className="text-sm font-semibold text-gray-600">Close</button>
        </div>
        <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <label className="sm:col-span-2 text-sm font-semibold text-gray-700">
            Creditor name
            <input value={form.account_name || ''} onChange={(e) => update('account_name', e.target.value)} className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 font-normal" />
          </label>
          <label className="sm:col-span-2 text-sm font-semibold text-gray-700">
            Street or P.O. box
            <input value={form.street1 || ''} onChange={(e) => update('street1', e.target.value)} className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 font-normal" />
          </label>
          <label className="sm:col-span-2 text-sm font-semibold text-gray-700">
            Address line 2
            <input value={form.street2 || ''} onChange={(e) => update('street2', e.target.value)} className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 font-normal" />
          </label>
          <label className="text-sm font-semibold text-gray-700">City<input value={form.city || ''} onChange={(e) => update('city', e.target.value)} className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 font-normal" /></label>
          <label className="text-sm font-semibold text-gray-700">State<input value={form.state || ''} onChange={(e) => update('state', e.target.value)} className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 font-normal" /></label>
          <label className="text-sm font-semibold text-gray-700">ZIP code<input value={form.zip_code || ''} onChange={(e) => update('zip_code', e.target.value)} className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 font-normal" /></label>
          <label className="text-sm font-semibold text-gray-700">
            Address use
            <select value={form.address_type || 'general'} onChange={(e) => update('address_type', e.target.value)} className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 font-normal">
              {addressTypes.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
            </select>
          </label>
          <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 sm:col-span-2">
            <input type="checkbox" checked={Boolean(form.is_verified)} onChange={(e) => update('is_verified', e.target.checked)} className="h-4 w-4 rounded border-gray-300 text-indigo-600" />
            I have reviewed and verified this entry
          </label>
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <SmallButton onClick={onClose} disabled={saving}>Cancel</SmallButton>
          <SmallButton type="submit" tone="primary" disabled={saving}>{saving ? 'Saving…' : 'Save changes'}</SmallButton>
        </div>
      </form>
    </div>
  );
}

export default function CommonCreditorCleanupDashboard() {
  const [isOpen, setIsOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [tab, setTab] = useState('duplicates');
  const [working, setWorking] = useState('');
  const [canonicalByGroup, setCanonicalByGroup] = useState({});
  const [matrixTarget, setMatrixTarget] = useState({});
  const [refreshExisting, setRefreshExisting] = useState(false);
  const [entryStatus, setEntryStatus] = useState('active');
  const [searchDraft, setSearchDraft] = useState('');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [editEntry, setEditEntry] = useState(null);

  const load = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ status: entryStatus, page: String(page), page_size: '50' });
      if (query) params.set('q', query);
      const payload = await apiRequest(`/firm/common-creditor-cleanup/?${params.toString()}`);
      setData(payload);
    } catch (requestError) {
      setError(requestError.message || 'Could not load the common-creditor cleanup dashboard.');
    } finally {
      if (!quiet) setLoading(false);
    }
  }, [entryStatus, page, query]);

  useEffect(() => {
    if (isOpen) load();
  }, [isOpen, load]);

  const runStatus = data?.matrix_study?.latest_run?.status;
  useEffect(() => {
    if (!isOpen || runStatus !== 'running') return undefined;
    const timer = window.setInterval(() => load({ quiet: true }), 5000);
    return () => window.clearInterval(timer);
  }, [isOpen, load, runStatus]);

  const defaults = useMemo(() => {
    const result = {};
    (data?.duplicates || []).forEach((group) => {
      const ordered = [...group.creditors].sort((a, b) => (
        Number(b.is_verified) - Number(a.is_verified)
        || (b.usage_count || 0) - (a.usage_count || 0)
      ));
      result[group.key] = ordered[0]?.id;
    });
    return result;
  }, [data?.duplicates]);

  const selectedCanonical = (group) => canonicalByGroup[group.key] || defaults[group.key];

  async function runAction(key, action, successMessage) {
    if (working) return;
    setWorking(key);
    setError('');
    setNotice('');
    try {
      await action();
      setNotice(successMessage);
      await load({ quiet: true });
    } catch (requestError) {
      setError(requestError.message || 'The requested change could not be saved.');
    } finally {
      setWorking('');
    }
  }

  async function mergeGroup(group) {
    const canonicalId = selectedCanonical(group);
    const sourceIds = group.creditors.filter((item) => item.id !== canonicalId).map((item) => item.id);
    await runAction(
      `merge-${group.key}`,
      () => apiRequest('/firm/common-creditor-cleanup/merge/', {
        method: 'POST',
        body: JSON.stringify({ canonical_id: canonicalId, source_ids: sourceIds }),
      }),
      'The entries were merged. Old case creditors were not changed.',
    );
  }

  async function dismiss(key, kind) {
    await runAction(
      `dismiss-${key}`,
      () => apiRequest('/firm/common-creditor-cleanup/dismiss/', {
        method: 'POST',
        body: JSON.stringify({ key, kind }),
      }),
      'The recommendation was removed from the review list.',
    );
  }

  async function applyMatrixRecommendation(item) {
    const target = matrixTarget[item.key] || item.matched_creditors?.[0]?.id || null;
    await runAction(
      `matrix-${item.key}`,
      () => apiRequest('/firm/common-creditor-cleanup/matrix-address/', {
        method: 'POST',
        body: JSON.stringify({
          creditor_id: target,
          name: item.name,
          address: item.suggested_address,
          evidence_count: item.suggested_address.evidence_count,
          last_seen: item.suggested_address.last_seen,
        }),
      }),
      target
        ? 'The court address is now preferred. The old address was preserved as an alternate.'
        : 'The matrix entry was added for review as a new common creditor.',
    );
  }

  async function saveEntry(entry) {
    await runAction(
      `edit-${entry.id}`,
      () => apiRequest(`/firm/common-creditor-cleanup/entries/${entry.id}/`, {
        method: 'PATCH',
        body: JSON.stringify({ ...entry, action: 'update' }),
      }),
      'The common creditor was updated.',
    );
    setEditEntry(null);
  }

  async function entryAction(entry, action) {
    await runAction(
      `${action}-${entry.id}`,
      () => apiRequest(`/firm/common-creditor-cleanup/entries/${entry.id}/`, {
        method: 'PATCH',
        body: JSON.stringify({ action }),
      }),
      action === 'archive' ? 'The entry was archived and removed from autocomplete.' : 'The entry was restored.',
    );
  }

  async function startMatrixStudy() {
    await runAction(
      'matrix-study',
      () => apiRequest('/firm/common-creditor-cleanup/retrieve-last-year/', {
        method: 'POST',
        body: JSON.stringify({ refresh_existing: refreshExisting }),
      }),
      'The last-year court-matrix retrieval was queued.',
    );
  }

  const summary = data?.summary || {};
  const tabs = [
    ['duplicates', `Possible duplicates (${summary.duplicate_groups || 0})`],
    ['matrix', `Court address evidence (${summary.matrix_recommendations || 0})`],
    ['directory', 'Full directory'],
    ['history', 'Merge history'],
  ];

  return (
    <section className="overflow-hidden rounded-lg bg-white shadow">
      <button
        type="button"
        onClick={() => setIsOpen((current) => !current)}
        aria-expanded={isOpen}
        className="flex w-full flex-col gap-4 px-6 py-5 text-left hover:bg-gray-50 sm:flex-row sm:items-center sm:justify-between"
      >
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Common Creditor Cleanup</h2>
          <p className="mt-1 text-sm text-gray-600">
            Review duplicates, preserve aliases and alternate addresses, and study addresses used in recent court matrices.
          </p>
          {data && (
            <div className="mt-3 flex flex-wrap gap-2">
              <StatusPill tone={summary.duplicate_groups ? 'amber' : 'green'}>{summary.duplicate_groups || 0} duplicate groups</StatusPill>
              <StatusPill tone={summary.matrix_recommendations ? 'blue' : 'gray'}>{summary.matrix_recommendations || 0} court recommendations</StatusPill>
              <StatusPill tone="green">{summary.verified || 0} verified</StatusPill>
              <StatusPill>{summary.archived || 0} archived</StatusPill>
            </div>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2 text-sm font-semibold text-indigo-700">
          <span>{isOpen ? 'Close dashboard' : 'Open dashboard'}</span>
          <span className={`text-xl transition-transform ${isOpen ? 'rotate-180' : ''}`}>⌄</span>
        </div>
      </button>

      {isOpen && (
        <div className="border-t border-gray-200 px-6 py-6">
          {error && <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}
          {notice && <div className="mb-4 rounded-md border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">{notice}</div>}
          {loading && !data ? (
            <div className="py-10 text-center text-sm text-gray-600">Analyzing common creditors and recent matrices…</div>
          ) : data && (
            <>
              <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
                {[
                  ['Active entries', summary.active || 0],
                  ['Verified', summary.verified || 0],
                  ['Possible duplicate groups', summary.duplicate_groups || 0],
                  ['Court recommendations', summary.matrix_recommendations || 0],
                  ['Archived', summary.archived || 0],
                ].map(([label, value]) => (
                  <div key={label} className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                    <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</div>
                    <div className="mt-1 text-xl font-bold text-gray-900">{value}</div>
                  </div>
                ))}
              </div>

              <div className="mt-6 rounded-lg border border-blue-200 bg-blue-50 p-4">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                  <div>
                    <h3 className="font-semibold text-blue-950">Last 12 months of court mailing matrices</h3>
                    <p className="mt-1 text-sm text-blue-900">
                      {data.matrix_study.matrix_cases} of {data.matrix_study.eligible_cases} eligible cases have a saved matrix in this study.
                      {' '}{data.matrix_study.observed_labels} unique case-level labels were analyzed.
                    </p>
                    {data.matrix_study.latest_run && (
                      <p className="mt-2 text-xs text-blue-800">
                        Latest run: <span className="font-semibold capitalize">{data.matrix_study.latest_run.status}</span>
                        {' · '}{data.matrix_study.latest_run.summary}
                        {data.matrix_study.latest_run.result?.processed !== undefined
                          ? ` (${data.matrix_study.latest_run.result.processed}/${data.matrix_study.latest_run.result.total || 0})`
                          : ''}
                      </p>
                    )}
                  </div>
                  <div className="shrink-0">
                    <label className="mb-2 flex items-center gap-2 text-xs text-blue-900">
                      <input type="checkbox" checked={refreshExisting} onChange={(event) => setRefreshExisting(event.target.checked)} className="h-4 w-4 rounded border-blue-300 text-indigo-600" />
                      Retrieve fresh copies even when one is already saved
                    </label>
                    <SmallButton tone="primary" onClick={startMatrixStudy} disabled={working === 'matrix-study' || runStatus === 'running'}>
                      {runStatus === 'running' ? 'Retrieval in progress…' : 'Retrieve last-year matrices'}
                    </SmallButton>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex flex-wrap gap-2 border-b border-gray-200 pb-3">
                {tabs.map(([value, label]) => (
                  <button key={value} type="button" onClick={() => setTab(value)} className={`rounded-md px-3 py-2 text-sm font-semibold ${tab === value ? 'bg-indigo-100 text-indigo-800' : 'text-gray-600 hover:bg-gray-100'}`}>
                    {label}
                  </button>
                ))}
              </div>

              {tab === 'duplicates' && (
                <div className="mt-5 space-y-4">
                  {(data.duplicates || []).length === 0 && <div className="rounded-lg border border-green-200 bg-green-50 p-4 text-sm text-green-800">No undismissed duplicate groups were found.</div>}
                  {(data.duplicates || []).map((group) => {
                    const canonicalId = selectedCanonical(group);
                    return (
                      <div key={group.key} className="rounded-lg border border-gray-200 p-4">
                        <div className="flex flex-wrap items-center justify-between gap-3">
                          <div>
                            <div className="flex items-center gap-2">
                              <StatusPill tone={group.confidence === 'high' ? 'red' : 'amber'}>{group.confidence} confidence</StatusPill>
                              <span className="text-sm font-semibold text-gray-900">{group.reason}</span>
                            </div>
                            <p className="mt-1 text-xs text-gray-500">Choose the entry that should remain as the preferred autocomplete result.</p>
                          </div>
                          <div className="flex gap-2">
                            <SmallButton onClick={() => dismiss(group.key, 'duplicate')}>Keep separate</SmallButton>
                            <SmallButton tone="primary" disabled={working === `merge-${group.key}`} onClick={() => mergeGroup(group)}>
                              {working === `merge-${group.key}` ? 'Merging…' : 'Merge into selected'}
                            </SmallButton>
                          </div>
                        </div>
                        <div className="mt-4 grid grid-cols-1 gap-3 xl:grid-cols-2">
                          {group.creditors.map((creditor) => (
                            <CreditorSummary
                              key={creditor.id}
                              creditor={creditor}
                              selectable
                              selected={creditor.id === canonicalId}
                              onSelect={() => setCanonicalByGroup((current) => ({ ...current, [group.key]: creditor.id }))}
                            />
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {tab === 'matrix' && (
                <div className="mt-5 space-y-4">
                  {(data.matrix_recommendations || []).length === 0 && <div className="rounded-lg border border-green-200 bg-green-50 p-4 text-sm text-green-800">No undismissed court-address recommendations need review.</div>}
                  {(data.matrix_recommendations || []).map((item) => (
                    <div key={item.key} className="rounded-lg border border-gray-200 p-4">
                      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <h3 className="font-semibold text-gray-900">{item.name}</h3>
                            <StatusPill tone={item.status === 'different_address' ? 'amber' : 'blue'}>
                              {item.status === 'different_address' ? 'Different court address' : 'New candidate'}
                            </StatusPill>
                          </div>
                          <div className="mt-2 rounded-md bg-blue-50 px-3 py-2 text-sm text-blue-950">
                            <div className="font-semibold">Suggested bankruptcy-notice address</div>
                            <div className="mt-1">{formatAddress(item.suggested_address)}</div>
                            <div className="mt-1 text-xs text-blue-800">
                              Seen in {item.suggested_address.case_count} case(s) · {item.suggested_address.evidence_count} observation(s) · last seen {formatDate(item.suggested_address.last_seen)}
                            </div>
                          </div>
                          {item.matched_creditors?.length > 0 && (
                            <div className="mt-3">
                              <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">Apply to existing entry</label>
                              <select
                                value={matrixTarget[item.key] || item.matched_creditors[0].id}
                                onChange={(event) => setMatrixTarget((current) => ({ ...current, [item.key]: Number(event.target.value) }))}
                                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                              >
                                {item.matched_creditors.map((creditor) => <option key={creditor.id} value={creditor.id}>{creditor.account_name} — {formatAddress(creditor)}</option>)}
                              </select>
                            </div>
                          )}
                        </div>
                        <div className="flex shrink-0 flex-wrap gap-2">
                          <SmallButton onClick={() => dismiss(item.key, 'matrix')}>Dismiss</SmallButton>
                          <SmallButton tone="primary" disabled={working === `matrix-${item.key}`} onClick={() => applyMatrixRecommendation(item)}>
                            {item.matched_creditors?.length ? 'Use court address' : 'Add for review'}
                          </SmallButton>
                        </div>
                      </div>
                      {item.address_options?.length > 1 && (
                        <details className="mt-3 text-sm">
                          <summary className="cursor-pointer font-semibold text-indigo-700">View other addresses found in matrices</summary>
                          <div className="mt-2 space-y-2">
                            {item.address_options.slice(1).map((address, index) => (
                              <div key={`${item.key}-${index}`} className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-gray-700">
                                {formatAddress(address)} <span className="text-xs text-gray-500">· {address.case_count} case(s), last seen {formatDate(address.last_seen)}</span>
                              </div>
                            ))}
                          </div>
                        </details>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {tab === 'directory' && (
                <div className="mt-5">
                  <form
                    onSubmit={(event) => { event.preventDefault(); setPage(1); setQuery(searchDraft.trim()); }}
                    className="flex flex-col gap-3 md:flex-row"
                  >
                    <input value={searchDraft} onChange={(event) => setSearchDraft(event.target.value)} placeholder="Search name or address" className="min-w-0 flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm" />
                    <select value={entryStatus} onChange={(event) => { setEntryStatus(event.target.value); setPage(1); }} className="rounded-md border border-gray-300 px-3 py-2 text-sm">
                      <option value="active">Active</option>
                      <option value="unverified">Unverified</option>
                      <option value="archived">Archived</option>
                      <option value="merged">Merged</option>
                      <option value="all">All</option>
                    </select>
                    <SmallButton type="submit" tone="primary">Search</SmallButton>
                  </form>
                  <div className="mt-4 overflow-x-auto rounded-lg border border-gray-200">
                    <table className="min-w-full text-left text-sm">
                      <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                        <tr><th className="px-4 py-3">Creditor</th><th className="px-4 py-3">Address</th><th className="px-4 py-3">Use</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Actions</th></tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {(data.entries?.results || []).map((entry) => (
                          <tr key={entry.id}>
                            <td className="px-4 py-3"><div className="font-semibold text-gray-900">{entry.account_name}</div>{entry.aliases?.length > 0 && <div className="mt-1 text-xs text-gray-500">Aliases: {entry.aliases.map((alias) => alias.name).join(', ')}</div>}</td>
                            <td className="px-4 py-3 text-gray-700">{formatAddress(entry)}<div className="mt-1 text-xs text-gray-500">{entry.address_type_label}</div></td>
                            <td className="px-4 py-3 text-gray-700">{entry.usage_count || 0} exact<div className="text-xs text-gray-500">{entry.name_usage_count || 0} by name</div></td>
                            <td className="px-4 py-3"><div className="flex flex-wrap gap-1">{entry.is_verified && <StatusPill tone="green">Verified</StatusPill>}{entry.is_archived && <StatusPill tone="red">Archived</StatusPill>}{entry.merged_into && <StatusPill tone="amber">Merged</StatusPill>}{!entry.is_verified && !entry.is_archived && <StatusPill>Needs review</StatusPill>}</div></td>
                            <td className="px-4 py-3"><div className="flex flex-wrap gap-2">{!entry.merged_into && <SmallButton onClick={() => setEditEntry(entry)}>Edit</SmallButton>}{entry.is_archived && !entry.merged_into ? <SmallButton tone="success" onClick={() => entryAction(entry, 'restore')}>Restore</SmallButton> : !entry.is_archived ? <SmallButton tone="danger" onClick={() => entryAction(entry, 'archive')}>Archive</SmallButton> : null}</div></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="mt-4 flex items-center justify-between text-sm text-gray-600">
                    <span>{data.entries?.count || 0} entries · page {data.entries?.page || 1} of {data.entries?.pages || 1}</span>
                    <div className="flex gap-2"><SmallButton disabled={page <= 1} onClick={() => setPage((value) => Math.max(1, value - 1))}>Previous</SmallButton><SmallButton disabled={page >= (data.entries?.pages || 1)} onClick={() => setPage((value) => value + 1)}>Next</SmallButton></div>
                  </div>
                </div>
              )}

              {tab === 'history' && (
                <div className="mt-5 space-y-3">
                  {(data.merge_history || []).length === 0 && <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-700">No common-creditor merges have been recorded.</div>}
                  {(data.merge_history || []).map((item) => (
                    <div key={item.id} className="flex flex-col gap-3 rounded-lg border border-gray-200 p-4 sm:flex-row sm:items-center sm:justify-between">
                      <div><div className="font-semibold text-gray-900">Merged into {item.canonical_name}</div><div className="mt-1 text-sm text-gray-600">{item.merged_names.join(', ')} · {new Date(item.created_at).toLocaleString()}</div></div>
                      {item.undone_at ? <StatusPill>Undone {new Date(item.undone_at).toLocaleDateString()}</StatusPill> : <SmallButton tone="danger" disabled={working === `undo-${item.id}`} onClick={() => runAction(`undo-${item.id}`, () => apiRequest(`/firm/common-creditor-cleanup/merge/${item.id}/undo/`, { method: 'POST', body: '{}' }), 'The merge was undone and the original entries were restored.')}>Undo merge</SmallButton>}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}
      {editEntry && (
        <EditModal
          entry={editEntry}
          addressTypes={data?.address_types || []}
          saving={working === `edit-${editEntry.id}`}
          onClose={() => setEditEntry(null)}
          onSave={saveEntry}
        />
      )}
    </section>
  );
}
